import { useId } from 'react';
import './MpaLogo.css';

const BRAND = {
    gradientStart: '#0891b2',
    gradientEnd: '#06b6d4',
    shadow: '#0e7490',
};

const LOCKUP_RATIO = 140 / 44;

function LogoDefs({ gradId, shadowId }) {
    return (
        <defs>
            <linearGradient id={gradId} x1="8" y1="0" x2="132" y2="44" gradientUnits="userSpaceOnUse">
                <stop stopColor={BRAND.gradientStart} />
                <stop offset="1" stopColor={BRAND.gradientEnd} />
            </linearGradient>
            <filter id={shadowId} x="-8%" y="-20%" width="116%" height="150%">
                <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor={BRAND.shadow} floodOpacity="0.28" />
            </filter>
        </defs>
    );
}

/** Square icon — favicon, sidebar, compact spaces */
export function MpaLogoIcon({ size = 32, className = '' }) {
    const gradId = useId().replace(/:/g, '');
    const shadowId = useId().replace(/:/g, '');

    return (
        <svg
            className={`mpa-logo__mark ${className}`.trim()}
            width={size}
            height={size}
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="MPA logo"
        >
            <LogoDefs gradId={gradId} shadowId={shadowId} />
            <g filter={`url(#${shadowId})`}>
                <rect width="48" height="48" rx="13" fill={`url(#${gradId})`} />
                <rect x="1" y="1" width="46" height="46" rx="12" stroke="white" strokeOpacity="0.22" />
                <rect x="10" y="8" width="28" height="20" rx="7" fill="white" fillOpacity="0.16" />
                <path
                    d="M16 17.5L20 21.5L32 10.5"
                    stroke="white"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <text
                    x="24"
                    y="40"
                    textAnchor="middle"
                    fill="white"
                    fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                    fontSize="11.5"
                    fontWeight="800"
                    letterSpacing="2.2"
                >
                    MPA
                </text>
            </g>
        </svg>
    );
}

/** Horizontal lockup — header, login, primary brand */
export function MpaLogoMark({ size = 36, className = '', tone = 'brand' }) {
    const gradId = useId().replace(/:/g, '');
    const shadowId = useId().replace(/:/g, '');
    const width = size * LOCKUP_RATIO;
    const onBrand = tone === 'onBrand';

    return (
        <svg
            className={`mpa-logo__mark mpa-logo__mark--lockup ${onBrand ? 'mpa-logo__mark--on-brand' : ''} ${className}`.trim()}
            width={width}
            height={size}
            viewBox="0 0 140 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="img"
            aria-label="MPA logo"
        >
            <LogoDefs gradId={gradId} shadowId={shadowId} />
            <g filter={`url(#${shadowId})`}>
                <rect
                    width="140"
                    height="44"
                    rx="13"
                    fill={onBrand ? '#ffffff' : `url(#${gradId})`}
                />
                <rect
                    x="1"
                    y="1"
                    width="138"
                    height="42"
                    rx="12"
                    stroke={onBrand ? 'rgba(8, 145, 178, 0.12)' : 'white'}
                    strokeOpacity={onBrand ? 1 : 0.2}
                />
                <rect
                    x="7"
                    y="7"
                    width="30"
                    height="30"
                    rx="9"
                    fill={onBrand ? `url(#${gradId})` : 'white'}
                    fillOpacity={onBrand ? 1 : 0.17}
                />
                <path
                    d="M15 22.5L19.5 27L29 17.5"
                    stroke="white"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                {!onBrand && (
                    <rect x="14" y="29" width="16" height="2.2" rx="1.1" fill="white" fillOpacity="0.55" />
                )}
                <line
                    x1="46"
                    y1="11"
                    x2="46"
                    y2="33"
                    stroke={onBrand ? '#0891b2' : 'white'}
                    strokeOpacity={onBrand ? 0.18 : 0.22}
                    strokeWidth="1"
                />
                <text
                    x="54"
                    y="29.5"
                    fill={onBrand ? '#0f172a' : 'white'}
                    fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                    fontSize="21"
                    fontWeight="800"
                    letterSpacing="3"
                >
                    MPA
                </text>
            </g>
        </svg>
    );
}

export default function MpaLogo({
    size = 36,
    variant = 'lockup',
    tone = 'brand',
    showText = false,
    showTagline = false,
    tagline = 'My Personal Assistant',
    layout = 'horizontal',
    textVariant = 'default',
    title = 'MPA',
    className = '',
    textClassName = '',
}) {
    const isVertical = layout === 'vertical';
    const isLight = textVariant === 'light';
    const Mark = variant === 'icon' ? MpaLogoIcon : MpaLogoMark;
    const markProps = variant === 'icon' ? { size } : { size, tone };

    return (
        <div
            className={`mpa-logo ${isVertical ? 'mpa-logo--vertical' : ''} ${className}`.trim()}
        >
            <Mark {...markProps} />
            {showText && (
                <div className={`mpa-logo__text ${textClassName}`.trim()}>
                    <span
                        className={`mpa-logo__title ${isLight ? 'mpa-logo__title--light' : ''}`}
                        style={{ fontSize: Math.max(16, Math.round(size * 0.56)) }}
                    >
                        {title}
                    </span>
                    {showTagline && tagline ? (
                        <span className={`mpa-logo__tagline ${isLight ? 'mpa-logo__tagline--light' : ''}`}>
                            {tagline}
                        </span>
                    ) : null}
                </div>
            )}
            {!showText && showTagline && tagline ? (
                <span className={`mpa-logo__tagline ${isLight ? 'mpa-logo__tagline--light' : ''} ${isVertical ? 'mpa-logo__tagline--below' : 'mpa-logo__tagline--beside'}`}>
                    {tagline}
                </span>
            ) : null}
        </div>
    );
}
